import { useState, useMemo, useEffect, useCallback, useContext, useRef } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { FormattedMessage, useIntl } from 'react-intl'

import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import { FlightsClassTypes, fareClassToUrlMap, isRewardFareClass, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import {
  CHARTER_BUNDLE_BADGE_MESSAGE,
  MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG,
  MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_DISPLAY,
  MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF,
  MALDIVES_MARKETING_BANNER_IMAGE_ID,
  MALDIVES_DREAM_TILE_TITLE_MESSAGES,
  MALDIVES_DREAM_TILES,
} from 'constants/charterFlight'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import HeroImage from 'content/components/HeroImage'
import Pill from 'LuxKit/Pill'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TextButton from 'LuxKit/Button/TextButton'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineSmileIcon from 'LuxKit/Icons/line/LineSmileIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import TextLink from 'LuxKit/TextLink'
import config from 'constants/config'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import ModalContext from 'contexts/ModalContext'
import MaldivesCharterFilters from './MaldivesDirectSearchFilters'
import MaldivesDirectSearchCalendarModal from './MaldivesDirectSearchCalendarModal'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import useOfferList from 'hooks/Offers/useOfferList'
import HotelPriceToggle from 'components/Hotels/HotelPriceToggle'
import { parseAsBoolean, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs'
import { ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { buildSuggestedDatesParamsKey, encodeChildrenOccupancy, parseOccupancyFromParams } from 'lib/search/searchUtils'
import { queryNullChildren } from 'constants/search'
import useFlightAndHotelBundle from 'hooks/FlightAndHotelBundle/useFlightAndHotelBundle'
import Places from 'constants/places'
import ReturnCharterFlightTile from 'components/Flights/CharterBundle/ReturnCharterFlightTile'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { OFFER_SET_SEARCH_RESULT_METADATA } from 'actions/actionConstants'
import { getDefaultAirport } from 'selectors/flightsSelectors'
import { useElementWidth } from 'hooks/useElementSize'
import {
  useMaldivesUspGroup1,
  useMaldivesUspGroup2,
  useMaldivesUspMobile,
  CharterPartnerIcon,
  MALDIVES_USP_IDS,
} from 'components/MaldivesCharter/MaldivesCharterUsp/MaldivesCharterUspItems'
import CharterBundleLandingPageUspItem from 'components/MaldivesCharter/MaldivesCharterUsp/CharterBundleLandingPageUspItem'
import { useFlightItemData } from './hooks/useFlightItemData'
import { filterDateRangesByNights } from './hooks/utils'
import { LUXURY_ESCAPES } from 'constants/brands'
import MaldivesCallPrompt from './MaldivesCallPrompt'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { EmptyArray } from 'lib/array/arrayUtils'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import useRebrandVersion from 'hooks/useRebrandVersion'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { MaldivesDirectBundleProvider } from './MaldivesDirectBundleContext'
import { isSkyLoungeCompatible } from 'lib/flights/flightUtils'
import MaldivesCharterFlightFareClassModal from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterFlightFareClassModal'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import {
  ALL_CHARTER_FARE_CLASSES,
  getCharterFareClassPolicy,
  getDefaultFareClassFromAllowed,
  getUnionAllowedFareClasses,
} from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { hasFeederFlightPriced as hasFeederFlightPricedHelper } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import zIndex from 'styles/tools/z-index'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { isLEStaff } from 'selectors/accountSelectors'
import DreamBlock from 'components/Pages/LandingPage/Hub/AdditionalGuides/DreamBlock'
import OfferSortControl from 'components/Common/OfferSortControl'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { replace } from 'lib/routing/appRouting'
import { pushWithRegion } from 'actions/NavigationActions'
import { useMaldivesDirectHotelStreamSearch } from './hooks/useMaldivesDirectHotelStreamSearch'
import AnywhereFilterTabs from 'components/Pages/HotelSearchPage/AnywhereFilterTabs'
import FriendsAndFamilyClaimModal from 'components/FriendsAndFamilyDiscount/FriendsAndFamilyClaimModal'
import { FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM } from 'constants/friendsAndFamilyDiscount'
import MaldivesCharterRewardSeatsInfoModal from 'components/Flights/CharterBundle/MaldivesCharterRewardSeatsInfoModal'
import useHashSignal from 'hooks/useHashSignal'
import MaldivesDirectHundredDollarSaleHeroCard from './MaldivesDirectHundredDollarSaleHeroCard'
import { buildMaldivesDirectRoomsForPassengerCount, getMaldivesBundleCheckOutDate, getMaldivesDirectPassengerCountForHolidayTypes } from './MaldivesDirectUtils'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

type BundleType = '5-nights' | '12-nights'
type OfferMode = 'hotels' | 'tourBundles'

const OPEN_DATE_SELECTION_QUERY_PARAM = 'openDateSelection'
const ENABLED_QUERY_PARAM_VALUES = new Set(['true', '1'])
const HUNDRED_DOLLAR_SALE_FULL_TERMS_URL = 'https://luxuryescapes.com/au/promotions'

const MELBOURNE_AIRPORT: App.Airport = {
  code: 'MEL',
  name: 'Tullamarine',
  latitude: -37.8136,
  longitude: 144.9631,
  countryCode: 'AU',
}

const StyledHeroImage = styled(HeroImage)<{ $hasMobileDiscountChip: boolean; $hasMobileReturnFlightFreeMessage: boolean }>`
  position: relative;
  min-height: ${props => rem(props.$hasMobileReturnFlightFreeMessage ? 260 : props.$hasMobileDiscountChip ? 240 : 216)};
  justify-content: flex-start;

  /* Mobile: solid blue background with image at bottom */
  background-color: #0280B3;

  @media (max-width: 369px) {
    aspect-ratio: 3 / 2;
  }

  ${mediaQueryUp.tablet} {
    min-height: ${rem(400)};
    justify-content: center;
    background-color: transparent;
  }


  > div:first-child {
    ${mediaQueryUp.tablet} {
      top: 0;
    }
  }

  /* Blue gradient overlay */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    inset-inline: 0;
    pointer-events: none;
    background: linear-gradient(0deg, rgba(2, 128, 179, 0.25) 0%, rgba(2, 128, 179, 0.65) 50%, rgba(2, 128, 179, 0.80) 100%);

    ${mediaQueryUp.tablet} {
      top: 0;
      background: linear-gradient(270deg, rgba(2, 128, 179, 0.00) 28.3%, rgba(2, 128, 179, 0.675) 89.24%);
    }
  }

`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: ${rem(1280)};
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  align-items: start;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    gap: ${rem(48)};
    margin-bottom: 0;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1.5fr 1fr;
    gap: ${rem(64)};
  }
`

// Apply text shadow to all hero text for readability over background image
const HeroText = styled.div`
  & h1, & h2, & h3, & h4, & p, & span {
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`

const HeroReturnFlightNote = styled(BodyText)`
  display: none;
  max-width: ${rem(660)};

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const HeroCtaRow = styled(Group)`
  margin-top: ${rem(-8)};
`

const HeroPhoneLink = styled.a`
  color: ${props => props.theme.palette.neutral.surface.staticWhite};
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`

const DesktopFamilyAndFriendsDiscountChip = styled(AssistChip)`
  display: none;

  ${mediaQueryUp.desktop} {
    display: inline-flex;
    position: absolute;
    z-index: 2;
    top: ${rem(-40)};
    inset-inline-end: 0;
  }
`

const MobileFamilyAndFriendsDiscountChip = styled(AssistChip)``

const CTACard = styled(Pane)`
  /* Pane already provides background-color via normal-background default */
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const CTACardMobile = styled(Pane)`
  --maldives-direct-mobile-card-inline-translate: -50%;

  position: absolute;
  inset-inline-start: 50%;
  bottom: 0;
  transform: translate(var(--maldives-direct-mobile-card-inline-translate), 50%);
  z-index: 1;
  width: min(calc(100% - ${rem(24)}), ${rem(351)});
  box-shadow: ${props => props.theme.shadow.bottom.small};

  [dir='rtl'] & {
    --maldives-direct-mobile-card-inline-translate: 50%;
  }
`

const HeroCtaHeadingGroup = styled(Group)`
  > * {
    white-space: nowrap;
  }
`

const HundredDollarSaleHeroCardMobile = styled(MaldivesDirectHundredDollarSaleHeroCard)`
  --maldives-direct-mobile-card-inline-translate: -50%;

  position: absolute;
  inset-inline-start: 50%;
  bottom: 10px;
  transform: translate(var(--maldives-direct-mobile-card-inline-translate), 50%);
  z-index: 1;
  width: min(calc(100% - ${rem(24)}), ${rem(351)});

  [dir='rtl'] & {
    --maldives-direct-mobile-card-inline-translate: 50%;
  }
`

// USP Section Styles
const USPSection = styled.section<{ $hasMobileReturnFlightFreeCard: boolean }>`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};

  body.${REBRAND_PALETTE_CLASS} & {
    background-color: var(--palette-highlight-primary-dark-background);
    color: var(--palette-highlight-primary-dark-contrast);
  }

  /* Add padding-top on mobile to accommodate overlapping CTACard */
  padding-block: ${props => rem(props.$hasMobileReturnFlightFreeCard ? 124 : 100)} ${rem(32)};
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(32)} 0;
  }

  /* Control br visibility in section headings */
  br {
    display: block;

    ${mediaQueryUp.tablet} {
      display: none;
    }
  }

  /* Main heading: wrap on mobile, no wrap on tablet+ */
  > div > div > h2 {
    white-space: normal;

    ${mediaQueryUp.tablet} {
      white-space: nowrap;
    }
  }
`

const USPGrid = styled.div<{ $itemCount: number }>`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${rem(24)} ${rem(16)};
  justify-content: center;

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${({ $itemCount }) => $itemCount >= 4
    ? 'repeat(4, minmax(0, 1fr))'
    : `repeat(${$itemCount}, minmax(0, ${rem(264)}))`};
    gap: ${rem(32)};
  }
`

const USPFooter = styled(Group)`
  margin-top: ${rem(40)};
  text-align: center;
`

const ModeToggleBottomSpacer = styled.div`
  height: ${rem(16)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};

  body.${REBRAND_PALETTE_CLASS} & {
    background-color: var(--palette-highlight-primary-dark-background);
  }

  ${mediaQueryUp.tablet} {
    height: ${rem(28)};
  }
`

function MaldivesSupportPhoneNumber({ headingVariant = 'heading5' }: { headingVariant?: 'heading5' | 'heading3' }) {
  return (
    <CSSBreakpoint min="tablet">
      <TextLink
        href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
        startIcon={<LinePhoneIcon />}
        size="large"
        underline={false}
        block
      >
        <Heading variant={headingVariant} as="span">
          {MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_DISPLAY}
        </Heading>
      </TextLink>
    </CSSBreakpoint>
  )
}

// Offers Section Styles
const OffersSection = styled.section`
  padding: ${rem(32)} 0;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const OffersContainer = styled(LayoutContainer)`
  max-width: ${rem(1200)};
  padding-inline: 0;

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(20)};
  }
`

const MobileFilterTabsPadding = styled.div`
  /* The inner CardCarousel uses negative horizontal margins to bleed past its parent's gutter,
   * which only works when both sides are padded by the carousel gutter width. With left-only
   * padding the right-side negative margin overshoots the viewport and causes mobile horizontal
   * wobble (clipped by body's overflow-x: hidden, but the layout still shifts). Use
   * padding-inline so both sides match. */
  ${mediaQueryDown.tablet} {
    padding-inline: ${rem(20)};
  }
`

const OffersHeader = styled(Group)`
  margin-bottom: ${rem(24)};

  ${mediaQueryDown.tablet} {
    padding-inline: ${rem(20)};
  }
`

const OfferModeToggleWrapper = styled.div`
  position: relative;
  margin: 0 0 ${rem(16)};
  z-index: 1;

  ${mediaQueryUp.tablet} {
    --maldives-direct-sticky-controls-inline-translate: -50%;

    position: absolute;
    top: 0;
    margin: 0;
    padding: 0 ${rem(12)};
    display: flex;
    justify-content: center;
    inset-inline-start: 50%;
    transform: translateY(-50%) translateX(var(--maldives-direct-sticky-controls-inline-translate));
    transition: transform 0.3s, top 0.3s;
    z-index: 1;

    [dir='rtl'] & {
      --maldives-direct-sticky-controls-inline-translate: 50%;
    }

    &.full-size {
      position: fixed;
      top: ${props => props.theme.layout.headerFloatingOffset};
      transform: translateY(0) translateX(var(--maldives-direct-sticky-controls-inline-translate));
      z-index: ${zIndex.stickyHeader};
    }
  }
`

const MobileOfferModeToggleStickyWrapper = styled(CSSBreakpoint)`
  position: sticky;
  top: ${props => props.theme.layout.headerFloatingOffset};
  /* No negative horizontal margins — the page root has no horizontal padding, so any negative
   * margin here pushes the toggle past the viewport edge and causes mobile horizontal-pan
   * wobble (clipped visually by body's overflow-x: hidden, but still affects layout). */
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  z-index: ${zIndex.stickyHeader};
`

const OfferModeToggleInner = styled.div`
  position: relative;
`

const MobileModeToggleButtons = styled(Group)`
  width: 100%;
`

const MobileModeTabButton = styled(TabButton)`
  flex: 1;
  min-width: 0;
`

const OfferModeTogglePane = styled(Pane)`
  position: absolute;
  top: 0;
  inset-inline: 0;
  bottom: 0;

  ${mediaQueryUp.tablet} {
    box-shadow: ${props => props.theme.shadow.bottom.medium};
    border-radius: 2px;
    transform: scaleX(1);
    transition: transform 0.3s;
    transform-origin: center top;

    &.full-size {
      transform: scaleX(var(--menu-scale, 1));
    }
  }
`

const ContactBannerContainer = styled(LayoutContainer)`
  margin-top: ${rem(30)};
`

const MALDIVES_DIRECT_OFFERS_SECTION_ID = 'maldives-direct-offers-heading'
const MALDIVES_DIRECT_SEARCH_FILTERS_HEADING_ID = 'maldives-direct-search-filters-heading'
const isMelbourneAirport = (airport?: App.Airport) => ['AVV', 'MBW', 'MEB', 'MELALL'].includes(airport?.code ?? 'MEL')

interface ModeToggleTabsProps {
  variant: 'desktop' | 'mobile'
  isTourBundleMode: boolean
  onHotelsModeClick: () => void
  onTourBundlesModeClick: () => void
  showFullSize?: boolean
  modeToggleScale?: number
  modeToggleRef?: React.RefObject<HTMLDivElement | null>
}

function ModeToggleTabs({
  variant,
  isTourBundleMode,
  onHotelsModeClick,
  onTourBundlesModeClick,
  showFullSize = false,
  modeToggleScale = 1,
  modeToggleRef,
}: ModeToggleTabsProps) {
  const intl = useIntl()
  const isMobile = variant === 'mobile'
  const hotelsMobileLabel = intl.formatMessage({
    id: 'qOtWA4_OicqojNu_wYq0',
    defaultMessage: 'Hotel + flight',
    description: 'Mode toggle label for hotel + flight on mobile in the maldives direct search page section',
  })
  const hotelsDesktopLabel = intl.formatMessage({
    id: 'fUfszU5WERwX82Ju-mM8',
    defaultMessage: 'Hotel + flight bundles',
    description: 'Mode toggle label for hotel + flight bundles on desktop',
  })
  const toursMobileLabel = intl.formatMessage({
    id: 'E-Mjs5oUdSQ9m6QW70la',
    defaultMessage: 'Tour + flight',
    description: 'Mode toggle label for tour + flight on mobile in the maldives direct search page section',
  })
  const toursDesktopLabel = intl.formatMessage({
    id: 's3G4_TeOzBMstPBo07c5',
    defaultMessage: 'Tour + flight bundles',
    description: 'Mode toggle label for tour + flight bundles on desktop',
  })
  const hotelsLabel = isMobile ? hotelsMobileLabel : hotelsDesktopLabel
  const toursLabel = isMobile ? toursMobileLabel : toursDesktopLabel

  if (isMobile) {
    return (
      <OfferModeToggleInner>
        <MobileModeToggleButtons direction="horizontal" gap={0}>
          <MobileModeTabButton
            size="medium"
            isActive={!isTourBundleMode}
            startIcon={<LineBedIcon />}
            onClick={onHotelsModeClick}
          >
            {hotelsLabel}
          </MobileModeTabButton>
          <MobileModeTabButton
            size="medium"
            isActive={isTourBundleMode}
            startIcon={<LineCompassIcon />}
            onClick={onTourBundlesModeClick}
          >
            {toursLabel}
          </MobileModeTabButton>
        </MobileModeToggleButtons>
      </OfferModeToggleInner>
    )
  }

  return (
    <OfferModeToggleInner ref={modeToggleRef}>
      <OfferModeTogglePane
        className={showFullSize ? 'full-size' : undefined}
        style={{ '--menu-scale': modeToggleScale } as React.CSSProperties}
      />
      <TabButtonsGroup fit="grow" tabletFit="auto">
        <TabButton
          size="medium"
          isActive={!isTourBundleMode}
          startIcon={<LineBedIcon />}
          onClick={onHotelsModeClick}
        >
          {hotelsLabel}
        </TabButton>
        <TabButton
          size="medium"
          isActive={isTourBundleMode}
          startIcon={<LineCompassIcon />}
          onClick={onTourBundlesModeClick}
        >
          {toursLabel}
        </TabButton>
      </TabButtonsGroup>
    </OfferModeToggleInner>
  )
}

function MaldivesDirectSearchPage() {
  const intl = useIntl()
  const isRebrand = useRebrandVersion() === 'rebrand'
  const formatAudCurrency = useCurrencyFormatter({ currency: 'AUD' })
  const formatAudCompactCurrency = useCurrencyFormatter({ currency: 'AUD', notation: 'compact' })
  const hundredDollarSaleFareAmount = formatAudCurrency(100, 'roundedDollar')
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const dreamTileReadMoreLabel = intl.formatMessage({
    id: 'bZfEQWN0g_O1NypsL6K3',
    defaultMessage: 'Read more',
    description: 'Link text for inspiration article cards in the Maldives dream block on the Maldives Direct search page',
  })
  const dreamTiles: Array<App.HubTile> = MALDIVES_DREAM_TILES.map(tile => ({
    ...tile,
    title: intl.formatMessage(
      MALDIVES_DREAM_TILE_TITLE_MESSAGES[tile.image],
      tile.image === 'maldives-your-way-from-no-expense-spared-to-under-$5k' ? { budget: formatAudCompactCurrency(5000, 'roundedDollar') } : undefined,
    ),
    linkText: dreamTileReadMoreLabel,
  }))
  const uspGroup1 = useMaldivesUspGroup1()
  const uspGroup2 = useMaldivesUspGroup2()
  const mobileUsps = useMaldivesUspMobile()
  const isRegionSupported = useAppSelector(isMaldivesCharterRegionEnabled)
  const isFriendsAndFamilyPageEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterFnfPage)
  const isReturnFlightFreeFeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.maldivesCharterReturnFlightFree)
  const isCharterLaunchOffer100FeatureEnabled = useFeatureFlag(OptimizelyFeatureFlags.charterLaunchOffer100)
  const isStaff = useAppSelector(isLEStaff)
  const isCharterLaunchOffer100Enabled =
    isCharterLaunchOffer100FeatureEnabled && !isReturnFlightFreeFeatureEnabled && !isAgentHubEnabled && !isStaff
  const canShowFamilyAndFriendsDiscountChip = isStaff && isFriendsAndFamilyPageEnabled
  const canShowMobileFamilyAndFriendsDiscountChip = canShowFamilyAndFriendsDiscountChip && !isReturnFlightFreeFeatureEnabled
  const windowSearch = useAppSelector(state => state.router.location.search)
  const maldivesCharterEnabled = isRegionSupported && (config.BRAND === LUXURY_ESCAPES || agentHubOrSubBrandConfig.isEnabled)
  const dispatch = useAppDispatch()
  const openModal = useContext(ModalContext)
  const hasOpenedFriendsAndFamilyClaimModalRef = useRef(false)
  const hasOpenedDateSelectionModalRef = useRef(false)

  // Detect when user has scrolled past the hero banner
  const { ref: heroDetectorRef, inView: heroInView } = useInView({
    threshold: 0,
    initialInView: true,
  })

  // State to track if user has manually closed the prompt
  const [isCallPromptClosed, setIsCallPromptClosed] = useState(false)
  const modeToggleRef = useRef<HTMLDivElement | null>(null)
  const modeToggleWidth = useElementWidth(modeToggleRef as React.MutableRefObject<HTMLElement | null>)
  const [viewportWidth, setViewportWidth] = useState(0)

  // Flight and Hotel bundle management
  const { updateFlightData, updateSelectedDates } = useFlightAndHotelBundle()

  // Occupancy query params - encoded as two separate query params (adults, children)
  // Default: 2 adults, no children (encoded as "2" for adults, "none" for children)
  const [adultsParam, setAdultsParam] = useQueryState('adults', parseAsString.withDefault('2'))
  const [childrenParam, setChildrenParam] = useQueryState('children', parseAsString.withDefault(queryNullChildren))

  // Parse the query params into rooms array, ensuring teenagers field is included for consistency
  const rooms = useMemo<Array<App.Occupants>>(
    () => parseOccupancyFromParams(adultsParam, childrenParam).map(room => ({
      ...room,
      teenagers: room.teenagers ?? 0,
    })),
    [adultsParam, childrenParam],
  )

  // Helper function to update rooms (updates both query params)
  const setRooms = useCallback((newRooms: Array<App.Occupants>) => {
    const adultsString = newRooms.map(room => room.adults).join(',')
    const childrenString = newRooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(',')

    setAdultsParam(adultsString)
    setChildrenParam(childrenString)
  }, [setAdultsParam, setChildrenParam])

  // Departure airport - the user's retained airport (recently used, falling back to closest), or Melbourne
  const departureAirportOfUser = useAppSelector(getDefaultAirport)

  const isMelbourneDefaultAirport = isMelbourneAirport(departureAirportOfUser) || agentHubOrSubBrandConfig.isEnabled
  const [departureAirport, setDepartureAirport] = useState<App.Airport>(isMelbourneDefaultAirport ? MELBOURNE_AIRPORT : departureAirportOfUser ?? MELBOURNE_AIRPORT)
  const hasUserSelectedAirport = useRef(false)

  // The retained airport resolves after mount on full page loads (account/geo state hydrates
  // asynchronously), so without this sync the Melbourne fallback would stick for the whole visit
  useEffect(() => {
    if (hasUserSelectedAirport.current || agentHubOrSubBrandConfig.isEnabled || !departureAirportOfUser) {
      return
    }
    setDepartureAirport(isMelbourneAirport(departureAirportOfUser) ? MELBOURNE_AIRPORT : departureAirportOfUser)
  }, [departureAirportOfUser])

  // sync airport to redux when agent hub is enabled so offer pages inherit the correct airport
  useEffect(() => {
    if (agentHubOrSubBrandConfig.isEnabled && departureAirport.code === MELBOURNE_AIRPORT.code) {
      dispatch(setRecentlyUsedAirportCode(MELBOURNE_AIRPORT.code))
    }
  }, [departureAirport.code, dispatch])

  // Flight class - read raw URL value then map to internal type
  const { fareClassFromUrl: fareClass, setFareClassUrl: setFareClassRaw, cabinFilter: selectedCabin, setCabinFilter, rewardSeatsOnly, setRewardSeatsOnly, infiniteMemberOnly, setInfiniteMemberOnly } = useCharterFilterUrlState()
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const infiniteMemberOnlyForTier = isInfiniteTier && infiniteMemberOnly
  const setFareClassParam = useCallback(
    (fc: FareClassWithReward | null) => setFareClassRaw(fc ? fareClassToUrlMap[fc] : null),
    [setFareClassRaw],
  )
  // Stripped to base cabin for downstream search/analytics calls (only the modal/banner cares about
  // the reward variant, and they read `fareClass` directly).
  const fareClassForSearch: FlightsClassTypes = fareClass ? toBaseFareClass(fareClass) : FlightsClassTypes.ECONOMY

  // Bundle selection - '5-nights' or '12-nights'
  const [selectedBundle, setSelectedBundle] = useQueryState(
    'bundle',
    parseAsStringEnum<BundleType>(['5-nights', '12-nights'] as const).withDefault('5-nights'),
  )

  // LuxPlus toggle
  // Default to false if brand is not Luxury Escapes
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(config.BRAND === LUXURY_ESCAPES),
  )

  // Price display mode state (persisted in URL)
  const [priceMode, setPriceMode] = useQueryState(
    'priceMode',
    parseAsStringEnum<'total' | 'perPerson'>(['total', 'perPerson'] as const).withDefault('total'),
  )
  const [offerMode, setOfferMode] = useQueryState(
    'offerMode',
    parseAsStringEnum<OfferMode>(['hotels', 'tourBundles'] as const).withDefault('hotels'),
  )
  const isTourBundleModeRequested = offerMode === 'tourBundles'
  const modeToggleScale = useMemo(() => {
    if (!modeToggleWidth) {
      return 1
    }
    return viewportWidth / modeToggleWidth
  }, [modeToggleWidth, viewportWidth])
  const tourModeHolidayTimeTitle = intl.formatMessage({
    id: 'eTIClOro5ID6J9UaD9wS',
    defaultMessage: 'Expert-led itineraries',
    description: 'Tour mode USP title replacing maximise your holiday time',
  })
  const tourModeHolidayTimeDescription = intl.formatMessage({
    id: 'QxO2t5wqs4v5KaMaR06I',
    defaultMessage: 'Thoughtfully designed tours that combine must-see destinations with hidden gems, so you get the most from every day.',
    description: 'Tour mode USP description for expert-led itineraries',
  })
  const tourModeHolidayTimeMobileDescription = intl.formatMessage({
    id: 'p0VAyYxnmEXOf-Ea1mRa',
    defaultMessage: '<strong>Expert-led itineraries</strong> combining must-see destinations with hidden gems.',
    description: 'Tour mode mobile USP description for expert-led itineraries with bold lead-in',
  }, {
    strong: chunks => <strong>{chunks}</strong>,
  })
  const tourModeBundlesHeading = intl.formatMessage({
    id: 'w-ht91OUqcLgFTVsFBvg',
    defaultMessage: 'Exclusive tour + flight bundles',
    description: 'Heading shown above offers list when tour mode is active',
  })
  const familyAndFriendsDiscountLabel = intl.formatMessage({
    id: 'tgMHD5Q511ePE4ZaPcPM',
    defaultMessage: 'Family & Friends Discount',
    description: 'Desktop assist chip label shown on the Maldives direct page when a friends and family referral is active',
  })

  const handleFamilyAndFriendsDiscountClick = useCallback(() => {
    dispatch(pushWithRegion('/campaign/friends-and-family'))
  }, [dispatch])

  const [checkInDateParam, setCheckInDate] = useQueryState('checkIn', parseAsString)
  const [checkOutDateParam, setCheckOutDate] = useQueryState('checkOut', parseAsString)

  const checkInDate = checkInDateParam ?? undefined
  const checkOutDate = checkOutDateParam ?? undefined

  // Flight calendar modal state
  const [isFlightCalendarOpen, setIsFlightCalendarOpen] = useState(false)

  const [selectedMonth, setSelectedMonth] = useState<string>('')

  // Handle price mode change
  function handlePriceModeChange(newMode: 'total' | 'perPerson') {
    setPriceMode(newMode)
  }

  const handleHotelsModeClick = useCallback(() => {
    setOfferMode('hotels')
  }, [setOfferMode])

  const handleTourBundlesModeClick = useCallback(() => {
    setOfferMode('tourBundles')
  }, [setOfferMode])

  const handleBrowseAllOffersClick = useCallback(() => {
    document
      .getElementById(MALDIVES_DIRECT_OFFERS_SECTION_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  useEffect(() => {
    setViewportWidth(window.innerWidth)

    const handleViewportResize = () => {
      const nextViewportWidth = window.innerWidth
      setViewportWidth(previousViewportWidth => (
        previousViewportWidth === nextViewportWidth ? previousViewportWidth : nextViewportWidth
      ))
    }
    window.addEventListener('resize', handleViewportResize)

    return () => window.removeEventListener('resize', handleViewportResize)
  }, [])

  // Handle flight calendar modal
  function handleOpenFlightCalendar() {
    setIsFlightCalendarOpen(true)
  }

  const scrollToSearchFiltersHeading = useCallback(() => {
    document
      .getElementById(MALDIVES_DIRECT_SEARCH_FILTERS_HEADING_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  function handleCloseFlightCalendar() {
    setIsFlightCalendarOpen(false)
  }

  function handleBundleChange(bundle: BundleType) {
    setSelectedBundle(bundle)
    const nights = bundle === '5-nights' ? 5 : 12
    setCheckOutDate(getMaldivesBundleCheckOutDate(checkInDate, nights))
  }

  function handleAirportChange(airport: App.Airport) {
    hasUserSelectedAirport.current = true
    setDepartureAirport(airport)
    dispatch(setRecentlyUsedAirportCode(airport.code))
  }

  function handleFlightClassChange(flightClass: FareClassWithReward | null) {
    setFareClassParam(flightClass)
  }

  // Check Economy Extra compatibility when rooms are applied (not on every intermediate change)
  const checkAndResetFareClassIfNeeded = useCallback((appliedRooms: Array<App.Occupants>) => {
    if (fareClass === FlightsClassTypes.LUXROW && !isSkyLoungeCompatible(appliedRooms)) {
      setFareClassParam(FlightsClassTypes.ECONOMY)
    }
  }, [fareClass, setFareClassParam])

  function handleLuxPlusToggle(enabled: boolean) {
    setLuxPlusEnabled(enabled)
  }

  function handleDateSelect(departDate: string, returnDate: string) {
    setCheckInDate(departDate)
    setCheckOutDate(returnDate)
  }

  // Handle flight calendar date selection (from modal)
  function handleFlightDateSelect(option: { departDate: string, returnDate: string, checkIn: string, checkOut: string, fareClass?: FareClassWithReward }) {
    // Use checkIn and checkOut (inner dates) for hotel queries
    setCheckInDate(option.checkIn)
    setCheckOutDate(option.checkOut)
    // Persist the selected cabin class to the URL
    if (option.fareClass !== undefined) {
      setFareClassParam(option.fareClass)
    }
    handleCloseFlightCalendar()
  }

  function handleMonthChange(month: string) {
    setSelectedMonth(month)
  }

  // Handle duration change from flight calendar
  function handleDurationChangeFromCalendar(nights: number) {
    const bundle = nights === 5 ? '5-nights' : '12-nights'
    setSelectedBundle(bundle)

    setCheckOutDate(getMaldivesBundleCheckOutDate(checkInDate, nights))
  }

  // Calculate nights based on selected bundle
  const nights = selectedBundle === '5-nights' ? 5 : 12

  // Get charter flight dates (departDate/returnDate) from dateRangesByMonth
  const { departDate, returnDate } = useCharterFlightDates({
    checkInDate,
    nights,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    rooms,
    fareClass: fareClassForSearch,
    feederOriginAirportCode: departureAirport.code,
  })

  const searchParams = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const friendsAndFamilyReferralEmail = searchParams[FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM]
  const shouldOpenDateSelectionModal = ENABLED_QUERY_PARAM_VALUES.has(
    searchParams[OPEN_DATE_SELECTION_QUERY_PARAM]?.toLowerCase() ?? '',
  )

  useEffect(() => {
    if (!friendsAndFamilyReferralEmail || hasOpenedFriendsAndFamilyClaimModalRef.current) {
      return
    }

    hasOpenedFriendsAndFamilyClaimModalRef.current = true
    openModal(
      <FriendsAndFamilyClaimModal
        fnfEmailPrefix={friendsAndFamilyReferralEmail}
      />,
    )
  }, [friendsAndFamilyReferralEmail, openModal])

  const { clearHash: clearSocieteHash } = useHashSignal({
    hash: '#societe-rewards',
    onActivate: async() => {
      const viewDates = await openModal<boolean>(<MaldivesCharterRewardSeatsInfoModal />)
      clearSocieteHash()
      if (viewDates) {
        setIsFlightCalendarOpen(true)
      }
    },
  })

  const selectedHolidayTypesParam = searchParams.holidayTypes
  const previousSelectedHolidayTypesParamRef = useRef(selectedHolidayTypesParam)
  const selectedHolidayTypes = useMemo(() => {
    return selectedHolidayTypesParam?.split(',').filter(Boolean)
  }, [selectedHolidayTypesParam])
  const selectedHolidayTypePassengerCount = useMemo(
    () => getMaldivesDirectPassengerCountForHolidayTypes(selectedHolidayTypes),
    [selectedHolidayTypes],
  )
  useEffect(() => {
    if (previousSelectedHolidayTypesParamRef.current === selectedHolidayTypesParam) {
      return
    }

    previousSelectedHolidayTypesParamRef.current = selectedHolidayTypesParam

    if (selectedHolidayTypePassengerCount === undefined) {
      return
    }

    const nextRooms = buildMaldivesDirectRoomsForPassengerCount(selectedHolidayTypePassengerCount)
    setRooms(nextRooms)
    checkAndResetFareClassIfNeeded(nextRooms)
  }, [checkAndResetFareClassIfNeeded, selectedHolidayTypesParam, selectedHolidayTypePassengerCount, setRooms])
  const selectedAmenities = useMemo(() => {
    return searchParams.amenities?.split(',').filter(Boolean)
  }, [searchParams.amenities])

  const {
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_VALUE,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_PRICE_DESC,
    SORT_OPTION_CUSTOMER_RATING,
  } = useOfferListSortOptions()
  const maldivesSortOptions = useMemo(() => [
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_VALUE,
    SORT_OPTION_PRICE_DESC,
    SORT_OPTION_CUSTOMER_RATING,
  ], [SORT_OPTION_RECOMMENDED, SORT_OPTION_PRICE_ASC, SORT_OPTION_VALUE, SORT_OPTION_PRICE_DESC, SORT_OPTION_CUSTOMER_RATING])
  const handleSortChange = useCallback(({ search }: { search: string }) => {
    dispatch(replace({ search }))
  }, [dispatch])

  const tourBundlePageSize = 25
  const tourBundleFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2],
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    flexibleMonths: '',
    flexibleNights: '',
    limit: tourBundlePageSize,
    isStream: false,
    showEnrichedResult: true,
  }), [])
  const tourBundleOfferList = useOfferList(tourBundleFilters, {
    minRequiredOffers: tourBundlePageSize,
  })
  const hasTourBundleOffers = tourBundleOfferList.offerIds.length > 0
  const isTourBundleMode = isTourBundleModeRequested && hasTourBundleOffers

  const handleViewSaleDatesClick = useCallback(() => {
    if (isTourBundleMode) {
      setOfferMode('hotels')
      requestAnimationFrame(scrollToSearchFiltersHeading)
    } else {
      scrollToSearchFiltersHeading()
    }

    setIsFlightCalendarOpen(true)
  }, [isTourBundleMode, scrollToSearchFiltersHeading, setOfferMode])

  useEffect(() => {
    if (!shouldOpenDateSelectionModal || hasOpenedDateSelectionModalRef.current || isTourBundleMode) {
      return
    }

    hasOpenedDateSelectionModalRef.current = true
    setIsFlightCalendarOpen(true)
  }, [isTourBundleMode, shouldOpenDateSelectionModal])

  // Charter date ranges from flight API (feeds the date picker and calendar pricing).
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms ?? EmptyArray,
    fareClass: fareClassForSearch,
    disabled: (rooms ?? EmptyArray).length === 0 || isTourBundleMode,
    feederOriginAirportCode: departureAirport.code,
  })
  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, nights),
    [allDateRangesByMonth, nights],
  )

  const {
    bundleFilters,
    listKey,
    maldivesOfferIds,
    maldivesOfferCount,
    offers,
    isOffersLoading,
    hasMoreStreamingOffers,
    canStreamHotelOffers,
    minRequiredOffers,
    setOfferPage,
    pageSize,
  } = useMaldivesDirectHotelStreamSearch({
    checkInDate,
    checkOutDate,
    rooms: rooms ?? EmptyArray,
    fareClassForSearch,
    holidayTypes: selectedHolidayTypes,
    amenities: selectedAmenities,
    sortBy: searchParams.sortBy as App.OfferListSortByOptions | undefined,
    isTourBundleMode,
    feederOriginAirportCode: departureAirport.code,
    charterNumberOfNights: nights,
  })

  const displayedUspsCombined = useMemo(() => {
    const uspsCombined = [...uspGroup1, ...uspGroup2]
    if (!isTourBundleMode) {
      return uspsCombined
    }

    // FLY-5552: tours are non-refundable, so the "free cancellation / change of mind" tile must not appear in tour mode
    return uspsCombined
      .filter(uspItem => uspItem.uspId !== MALDIVES_USP_IDS.flexibleBookings)
      .map(uspItem => {
        if (uspItem.uspId === MALDIVES_USP_IDS.maximiseHolidayTime) {
          return {
            ...uspItem,
            title: tourModeHolidayTimeTitle,
            description: tourModeHolidayTimeDescription,
            icon: <LineGuidedtoursIcon />,
            tabletIcon: <LineGuidedtoursIcon size="XL" style={{ display: 'block' }} />,
          }
        }
        return uspItem
      })
  }, [uspGroup1, uspGroup2, isTourBundleMode, tourModeHolidayTimeTitle, tourModeHolidayTimeDescription])
  const displayedMobileUsps = useMemo(() => {
    if (!isTourBundleMode) {
      return mobileUsps
    }

    return mobileUsps
      .filter(uspItem => uspItem.uspId !== MALDIVES_USP_IDS.flexibleBookings)
      .map(uspItem => {
        if (uspItem.uspId === MALDIVES_USP_IDS.maximiseHolidayTime) {
          return {
            ...uspItem,
            title: tourModeHolidayTimeTitle,
            description: tourModeHolidayTimeMobileDescription,
            icon: <LineGuidedtoursIcon />,
          }
        }
        return uspItem
      })
  }, [mobileUsps, isTourBundleMode, tourModeHolidayTimeTitle, tourModeHolidayTimeMobileDescription])
  const allowedFareClassesByOfferId = useMemo(() => {
    const map = new Map<string, Array<FlightsClassTypes>>()
    offers.forEach((offer) => {
      if (!offer) {
        return
      }
      const policy = getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      map.set(offer.id, policy.allowedFareClasses)
    })
    return map
  }, [offers, rooms])
  const hasFareClassRestrictionsByOfferId = useMemo(() => {
    const map = new Map<string, boolean>()
    offers.forEach((offer) => {
      if (!offer) {
        return
      }
      const policy = getCharterFareClassPolicy({
        campaignTags: offer.campaignTags,
        occupancies: rooms,
      })
      map.set(offer.id, policy.taggedFareClasses.length > 0)
    })
    return map
  }, [offers, rooms])
  const availableFareClasses = useMemo(() => {
    const union = getUnionAllowedFareClasses({ offers, occupancies: rooms })
    if (union.length > 0) {
      return union
    }
    const policy = getCharterFareClassPolicy({
      campaignTags: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
      occupancies: rooms,
    })
    return policy.allowedFareClasses
  }, [offers, rooms])
  const defaultAvailableFareClass = useMemo(
    () => getDefaultFareClassFromAllowed(availableFareClasses),
    [availableFareClasses],
  )
  const effectiveAvailableFareClasses = useMemo(() => {
    if (availableFareClasses.length > 0) {
      return availableFareClasses
    }
    return ALL_CHARTER_FARE_CLASSES
  }, [availableFareClasses])
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(effectiveAvailableFareClasses)
  const canOpenFareClassModal = useMemo(
    () => allowedFareClassesForModal.length > 1,
    [allowedFareClassesForModal.length],
  )
  const openFareClassModal = useCallback(async() => {
    if (!canOpenFareClassModal) {
      return
    }
    const hadTripDatesWhenOpening = Boolean(checkInDate && checkOutDate)
    const selectedClass = await openModal<FareClassWithReward>(<MaldivesCharterFlightFareClassModal
      selectedClassId={fareClass ?? FlightsClassTypes.ECONOMY}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      occupancies={rooms}
      nights={nights}
      luxPlusEnabled={luxPlusEnabled}
      allowedFareClasses={allowedFareClassesForModal}
      rewardSeatsOnly={rewardSeatsOnly}
      onRewardSeatsOnlyChange={setRewardSeatsOnly}
    />)
    if (selectedClass) {
      setFareClassParam(selectedClass)
      setCabinFilter(selectedClass)
      if (isRewardFareClass(selectedClass)) {
        setRewardSeatsOnly(true)
      }
      if (!hadTripDatesWhenOpening) {
        setIsFlightCalendarOpen(true)
      }
    }
  }, [canOpenFareClassModal, openModal, fareClass, checkInDate, checkOutDate, rooms, nights, luxPlusEnabled, allowedFareClassesForModal, setFareClassParam, setCabinFilter, setRewardSeatsOnly, rewardSeatsOnly])
  const mainFlightData = useFlightItemData({
    checkIn: checkInDate ?? '',
    checkOut: checkOutDate ?? '',
    rooms,
    fareClass: fareClassForSearch,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    feederOriginAirportCode: departureAirport.code,
  })
  const hasUserSelectedDates = !!(checkInDate && checkOutDate)
  // Only evaluated once dates are picked AND the main flight search has resolved.
  // Stays undefined otherwise so consumers can fall back to the configured-feeder gate.
  const hasFeederFlightPriced = hasUserSelectedDates && !mainFlightData.fetching
    ? hasFeederFlightPricedHelper(mainFlightData.departingFlight, mainFlightData.returningFlight)
    : undefined
  const hasReturnFareFree = hasUserSelectedDates && !mainFlightData.fetching
    ? mainFlightData.hasReturnFareFree
    : undefined
  const bundleContextValue = useMemo(() => ({
    charterBundle: true,
    isPricePerPerson: priceMode === 'perPerson',
    fareClass: fareClassForSearch,
    selectedFareClass: fareClass,
    departureAirportCode: departureAirport.code,
    departureAirportName: departureAirport.name,
    duration: nights,
    allowedFareClassesByOfferId,
    hasFareClassRestrictionsByOfferId,
    hasUserSelectedDates,
    hasFeederFlightPriced,
    hasReturnFareFree,
    canOpenFareClassModal,
    openFareClassModal,
    selectedCabin,
    rewardSeatsOnly,
    isPromotionPriceApplied: mainFlightData.isPromotionPriceApplied,
  }), [
    priceMode,
    fareClassForSearch,
    fareClass,
    departureAirport.code,
    departureAirport.name,
    nights,
    allowedFareClassesByOfferId,
    hasFareClassRestrictionsByOfferId,
    hasUserSelectedDates,
    hasFeederFlightPriced,
    hasReturnFareFree,
    canOpenFareClassModal,
    openFareClassModal,
    selectedCabin,
    rewardSeatsOnly,
    mainFlightData.isPromotionPriceApplied,
  ])
  // Only reset fare class when the user explicitly changes it (not from URL on page load).
  // Track whether the fare class has been changed by user interaction to avoid resetting
  // URL-provided selections when offers load.
  const fareClassUserChangedRef = useRef(false)
  const prevFareClassRef = useRef(fareClass)
  useEffect(() => {
    if (prevFareClassRef.current !== fareClass && prevFareClassRef.current !== undefined) {
      fareClassUserChangedRef.current = true
    }
    prevFareClassRef.current = fareClass
  }, [fareClass])
  useEffect(() => {
    if (!defaultAvailableFareClass || !fareClassUserChangedRef.current) {
      return
    }
    if (fareClass && !availableFareClasses.includes(toBaseFareClass(fareClass))) {
      setFareClassParam(defaultAvailableFareClass)
    }
  }, [availableFareClasses, defaultAvailableFareClass, fareClass, setFareClassParam])

  useEffect(() => {
    if (isTourBundleModeRequested && !tourBundleOfferList.fetching && !hasTourBundleOffers) {
      setOfferMode('hotels')
    }
  }, [isTourBundleModeRequested, tourBundleOfferList.fetching, hasTourBundleOffers, setOfferMode])
  useEffect(() => {
    if (isTourBundleMode && isFlightCalendarOpen) {
      setIsFlightCalendarOpen(false)
    }
  }, [isTourBundleMode, isFlightCalendarOpen])

  // Update Redux suggested dates when check-in/check-out changes so offer URLs use correct dates
  useEffect(() => {
    if (isTourBundleMode || !checkInDate || !checkOutDate || maldivesOfferIds.length === 0) {
      return
    }

    const suggestedDatesKey = buildSuggestedDatesParamsKey('', '', rooms)
    const offerSuggestedDates = maldivesOfferIds.reduce<Record<string, App.OfferSuggestedDates>>((acc, offerId) => {
      acc[offerId] = {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      }
      return acc
    }, {})

    const uniqueSearchTargetId = `maldives-direct-${Places.Maldives.id}`
    const uniqueAvailabilityKey = `maldives-direct-${suggestedDatesKey}`
    const uniqueOfferMetaDataKey = `maldives-direct-${listKey}`

    dispatch({
      type: OFFER_SET_SEARCH_RESULT_METADATA,
      data: {
        distanceFromSearchTarget: {
          [uniqueSearchTargetId]: {},
        },
        offerAvailabilityFromSearchTarget: {
          [uniqueAvailabilityKey]: {},
        },
        offerSuggestedDates: {
          [suggestedDatesKey]: offerSuggestedDates,
        },
        offerMetaData: {
          [uniqueOfferMetaDataKey]: {},
        },
      },
      searchTargetId: uniqueSearchTargetId,
      availabilityKey: uniqueAvailabilityKey,
      suggestedDatesKey,
      offerMetaDataKey: uniqueOfferMetaDataKey,
    })
  }, [dispatch, isTourBundleMode, checkInDate, checkOutDate, maldivesOfferIds, listKey, rooms])

  useEffect(() => {
    if (checkInDate) {
      setSelectedMonth(moment(checkInDate).format(ISO_DATE_SHORT_FORMAT))
    } else {
      const months = Object.keys(dateRangesByMonth)
      if (months.length > 0) {
        setSelectedMonth(months[0])
      }
    }
  }, [checkInDate, dateRangesByMonth])

  const offersHeaderNode = useMemo(() => {
    if (isOffersLoading) {
      return <BodyText variant="large">
        {intl.formatMessage({
          id: 'Gwf6vZLx0VkVILuxpBxT',
          defaultMessage: 'Loading offers…',
          description: 'Loading text while Maldives charter hotel offers are being fetched',
        })}
      </BodyText>
    }
    return (
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: '0gwKsC-kP5LNQpR66-Kr',
            defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
            description: 'Header text showing number of Maldives charter hotel offers found',
          },
          { count: maldivesOfferCount },
        )}
      </BodyText>
    )
  }, [isOffersLoading, maldivesOfferCount, intl])
  const tourOffersHeaderNode = useMemo(() => {
    if (tourBundleOfferList.fetching) {
      return <BodyText variant="large">
        {intl.formatMessage({
          id: 'OHpjLwCvgADt7aUGbNJ7',
          defaultMessage: 'Loading offers…',
          description: 'Loading text while Maldives charter tour offers are being fetched',
        })}
      </BodyText>
    }
    return (
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: 'hG3y5JBXM9U8EbMe0yYX',
            defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
            description: 'Header text showing number of Maldives charter tour offers found',
          },
          { count: tourBundleOfferList.offerIds.length },
        )}
      </BodyText>
    )
  }, [tourBundleOfferList.fetching, tourBundleOfferList.offerIds.length, intl])

  // Sync bundle data to Redux store (only when dates are selected)
  useEffect(() => {
    if (isTourBundleMode) {
      return
    }
    if (checkInDate && checkOutDate) {
      updateSelectedDates({
        checkIn: checkInDate,
        checkOut: checkOutDate,
      })
    }

    updateFlightData({
      fetching: mainFlightData.fetching,
      totalPrice: mainFlightData.totalFlightFare,
      journey: mainFlightData.departingFlight,
    })
  }, [
    isTourBundleMode,
    checkInDate,
    checkOutDate,
    mainFlightData,
    updateFlightData,
    updateSelectedDates,
  ])

  const exclusiveToLEText = intl.formatMessage(CHARTER_BUNDLE_BADGE_MESSAGE)

  const heroHeadingMessage = (
    <FormattedMessage
      id="_6GHeVTwxFe2IQARogy1"
      defaultMessage="The Maldives, {br}with <em> more </em> time in paradise"
      description="Hero heading for Maldives Direct search page in the search interface"
      values={{
        em: (chunks) => <em>{chunks}</em>,
        br: <br />,
      }}
    />
  )
  const hurryLimitedSeatsText = intl.formatMessage({
    id: 'DzgicMX1YYZOLqMlI8eK',
    defaultMessage: 'Hurry, limited seats!',
    description: 'Urgency heading on Maldives charter hero CTA card in the maldives direct search page section',
  })
  const callOrBookText = intl.formatMessage({
    id: 'T6WXv73p-6T4BbC00gd6',
    defaultMessage: 'Call or book online.',
    description: 'CTA heading on Maldives charter hero card when phone support available',
  })
  const bookOnlineText = intl.formatMessage({
    id: 'aXUCMjV0RDAMQsPg_Hu1',
    defaultMessage: 'Book online.',
    description: 'CTA heading on Maldives charter hero card for agent hub users',
  })
  const talkToExpertText = intl.formatMessage({
    id: 'fUr-763MCXn4G3kvNp3J',
    defaultMessage: 'Talk to a Maldives travel expert today.',
    description: 'Body text encouraging contact with Maldives travel expert on hero CTA card',
  })
  const callPriorityLineText = intl.formatMessage({
    id: 'fOrJfpCYvMdcnwNVq215',
    defaultMessage: 'Call our priority phone line',
    description: 'Bold text prompting to call Maldives charter priority phone line',
  })
  const browseAllOffersText = intl.formatMessage({
    id: 'sy5cTyKtunE9OMODsMqD',
    defaultMessage: 'Browse all offers',
    description: 'Button text to scroll to all Maldives charter offers',
  })
  const browseAllBundlesText = intl.formatMessage({
    id: '-FeCha9ORkbI1m59SX9T',
    defaultMessage: 'Browse all bundles',
    description: 'Hero button text on the Maldives charter landing page that scrolls to all bundle offers',
  })
  const orText = intl.formatMessage({
    id: 'mIzyKaUczNCK-J3fIlOR',
    defaultMessage: 'OR',
    description: 'Separator text between the browse bundles button and phone number in the Maldives charter hero',
  })
  const callNowText = intl.formatMessage({
    id: 'U06qjUmrf-T14_XZdqq1',
    defaultMessage: 'Call now',
    description: 'Mobile button text to call Maldives charter support',
  })
  const contactBannerText = intl.formatMessage({
    id: 'LBwcJkNj6L-_WNjHEcSh',
    defaultMessage: 'Our Maldives travel experts can help with any questions',
    description: 'Contact banner text for Maldives charter support in the maldives direct search page section',
  })
  const returnFlightFreeHeroText = intl.formatMessage({
    id: 'VZ6f_f5rToZQUMNPwSTO',
    defaultMessage: 'Return for FREE Flight Sale',
    description: 'Hero card copy for the Maldives charter return flight for free promotion',
  })
  const returnFlightFreeAvailabilityText = intl.formatMessage({
    id: 'ku8rXy9cQA6nev1Ezd_t',
    defaultMessage: 'Book any Maldives hotel & flight bundle on select travel dates and your return flight is free.',
    description: 'Hero card supporting copy for the Maldives charter return flight for free promotion',
  })
  const returnFlightFreeSavingsText = intl.formatMessage({
    id: '0t7gJxH3IFJv8eAiym8A',
    defaultMessage: 'Save from $699 per person.',
    description: 'Hero card savings copy for the Maldives charter return flight for free promotion',
  })
  const viewEligibleDatesText = intl.formatMessage({
    id: 'AWB7NEEgI2C_2I8DhS_7',
    defaultMessage: 'View eligible dates',
    description: 'CTA button text opening eligible dates for the Maldives return flight for free promotion',
  })
  const returnFlightFreeNoteText = intl.formatMessage({
    id: 'Zuu5hKDCQjk0QDzleiHk',
    defaultMessage: 'Your return flight to Melbourne is on us. Look for the<lineBreak></lineBreak>Return Flight For Free badge on selected Maldives dates.',
    description: 'Italic helper text below the Maldives charter hero explaining how to find eligible dates',
  }, {
    lineBreak: () => <br key="return-flight-line-break" />,
  })
  const hundredDollarSaleTermsText = intl.formatMessage(
    {
      id: 'jMKG-GVENHsF57Cct0f0',
      defaultMessage: 'Promoter: Luxury Escapes Travel Pty Ltd (ABN 16 150 650 927). 100 economy class return flight tickets, MEL to MLE, {fareAmount} per ticket, available only as part of a Luxury Escapes Maldives bundle 7:00pm AEST on Monday 18 May 2026 to 11:59pm AEST on Monday 25 May 2026, or until the 100-ticket allocation sells out. Open to Australian residents. First come, first served. Limited seats. Additional costs apply per standard package inclusions and exclusions. Flight ticket is non-refundable. Not combinable with any other Luxury Escapes promotion, discount, credit, or offer. Bookings are only available from the commencement of the Offer Period; no bookings or reservations will be accepted prior to 7:00pm AEST on Monday 18 May 2026.',
      description: 'Mini terms and conditions for the Maldives charter hundred dollar sale',
    },
    { fareAmount: hundredDollarSaleFareAmount },
  )
  const hundredDollarSaleTermsTitle = intl.formatMessage(
    {
      id: 'E45Q-VjuLfEatoyIC4_y',
      defaultMessage: 'T&Cs (100 seats at {fareAmount} Sale)',
      description: 'Title for the Maldives charter hundred dollar sale mini terms and conditions',
    },
    { fareAmount: hundredDollarSaleFareAmount },
  )
  const disclaimerText = intl.formatMessage({
    id: 'a3_bUxyxhmM7uSAUglP7',
    defaultMessage: '*Based on average time savings compared with non-direct flights from Melbourne to Malé operated by competitor airlines. Calculation includes estimated total door-to-door travel time, encompassing flight duration, transfers, layovers and hotel check-in/check-out times.',
    description: 'Legal disclaimer about charter flight transit time savings comparison',
  })
  const selectTripDatesText = intl.formatMessage({
    id: 'oU0vVHgO-s0CqiSIjI2l',
    defaultMessage: 'Select your trip dates',
    description: 'Heading title for Maldives charter flight calendar modal',
  })

  const desktopHeroCtaCard = isReturnFlightFreeFeatureEnabled ? (
    <CTACard padding={20}>
      <Group direction="vertical" gap={8} tabletGap={16}>
        <Group direction="vertical" gap={8} horizontalAlign="center">
          <Heading variant="heading4" as="h4" align="center" colour="highlight-primary-dark">
            {returnFlightFreeHeroText}
          </Heading>
          <BodyText variant="large" align="center">
            {returnFlightFreeAvailabilityText}
          </BodyText>
          {!isAgentHubEnabled && (
            <BodyText variant="large" align="center">
              {returnFlightFreeSavingsText}
            </BodyText>
          )}
        </Group>
        <Group direction="horizontal" gap={8} horizontalAlign="stretch" fullWidth verticalAlign="center">
          {!isAgentHubEnabled && <TextButton
            kind="secondary"
            variant="default"
            size="large"
            startIcon={<LinePhoneIcon />}
            href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
            rel="nofollow"
            fit="full-width"
          >
            {callNowText}
          </TextButton>}
          <TextButton kind="primary" size="large" fit="full-width" onClick={handleViewSaleDatesClick}>
            {viewEligibleDatesText}
          </TextButton>
        </Group>
      </Group>
    </CTACard>
  ) : isCharterLaunchOffer100Enabled ? (
    <MaldivesDirectHundredDollarSaleHeroCard onViewSaleDates={handleViewSaleDatesClick} />
  ) : (
    <CTACard padding={20}>
      <Group direction="vertical" gap={8} tabletGap={16}>
        <Group direction="vertical" gap={8} horizontalAlign="center">
          <HeroCtaHeadingGroup direction="vertical" gap={8} horizontalAlign="center" desktopDirection="horizontal" desktopWrap="wrap" fullWidth>
            <Heading variant="heading4" as="h4" align="center" colour={isRebrand ? 'urgency' : 'highlight-primary-dark'}>
              {hurryLimitedSeatsText}
            </Heading>
            <Heading variant="heading4" as="h4" align="center" colour="normal-contrast">
              {!isAgentHubEnabled ? callOrBookText : bookOnlineText}
            </Heading>
          </HeroCtaHeadingGroup>
          {!isAgentHubEnabled && (
            <>
              <Group direction="vertical" gap={0} horizontalAlign="center">
                <BodyText variant="large" align="center">
                  {talkToExpertText}
                </BodyText>
                <BodyText variant="large" align="center" weight="bold">
                  {callPriorityLineText}
                </BodyText>
              </Group>

              <MaldivesSupportPhoneNumber headingVariant="heading3" />
            </>
          )}
        </Group>

        <TextButton kind="primary" size="large" fit="full-width" onClick={handleBrowseAllOffersClick}>
          {browseAllOffersText}
        </TextButton>
      </Group>
    </CTACard>
  )
  const mobileHeroCtaCard = isReturnFlightFreeFeatureEnabled ? (
    <CTACardMobile padding={20}>
      <Group direction="vertical" gap={8} tabletGap={16}>
        <Group direction="vertical" gap={12} horizontalAlign="center">
          <Heading variant="heading4" as="h4" colour="highlight-primary-dark">
            {returnFlightFreeHeroText}
          </Heading>
          <BodyText variant="medium" align="center">
            {returnFlightFreeAvailabilityText}
          </BodyText>
          {!isAgentHubEnabled && <BodyText variant="medium" align="center">
            {returnFlightFreeSavingsText}
          </BodyText>}
          <Group direction="horizontal" gap={8} horizontalAlign="stretch" fullWidth verticalAlign="center">
            {!isAgentHubEnabled && <TextButton
              kind="secondary"
              variant="default"
              startIcon={<LinePhoneIcon />}
              href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
              rel="nofollow"
              fit="full-width"
            >
              {callNowText}
            </TextButton>}
            <TextButton kind="primary" fit="full-width" onClick={handleViewSaleDatesClick}>
              {viewEligibleDatesText}
            </TextButton>
          </Group>
        </Group>
      </Group>
    </CTACardMobile>
  ) : isCharterLaunchOffer100Enabled ? (
    <HundredDollarSaleHeroCardMobile onViewSaleDates={handleViewSaleDatesClick} />
  ) : (
    <CTACardMobile padding={20}>
      <Group direction="vertical" gap={8} tabletGap={16}>
        <Group direction="vertical" gap={12} horizontalAlign="center">
          <Heading variant="heading4" as="h4" colour="highlight-primary-dark">
            <FormattedMessage
              id="3PBCEvpwInZOcUYT19am"
              defaultMessage="<primary>Hurry, limited seats!</primary> <secondary>Call or book online.</secondary>"
              description="Mobile CTA heading with urgency message and call-to-action on Maldives charter page"
              values={{
                primary: (chunks) => <BodyText variant="large" as="span" colour={isRebrand ? 'urgency' : 'highlight-primary-dark'}>{chunks}</BodyText>,
                secondary: (chunks) => <BodyText variant="large" as="span" colour="normal-contrast">{chunks}</BodyText>,
              }}
            />
          </Heading>
          <Group direction="horizontal" gap={8} horizontalAlign="stretch" fullWidth verticalAlign="center">
            <TextButton
              kind="secondary"
              variant="default"
              startIcon={<LinePhoneIcon />}
              href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
              rel="nofollow"
              fit="full-width"
            >
              {callNowText}
            </TextButton>
            <TextButton kind="primary" fit="full-width" onClick={handleBrowseAllOffersClick}>
              {browseAllOffersText}
            </TextButton>
          </Group>
        </Group>
      </Group>
    </CTACardMobile>
  )

  if (!maldivesCharterEnabled) {
    // show 404 error page
    return <Error404PageWithOffers />
  }

  return (
    <div>
      <div ref={heroDetectorRef}>
        <StyledHeroImage
          id={MALDIVES_MARKETING_BANNER_IMAGE_ID}
          $hasMobileDiscountChip={canShowMobileFamilyAndFriendsDiscountChip}
          $hasMobileReturnFlightFreeMessage={isReturnFlightFreeFeatureEnabled}
          fit="bottom"
          gravity="center"
          mobileAspectRatio="7:6"
          tabletAspectRatio="9:5"
          desktopAspectRatio="18:5"
          largeDesktopAspectRatio="10:3"
        >
          <HeroContent>
            {canShowFamilyAndFriendsDiscountChip && <DesktopFamilyAndFriendsDiscountChip
              variant="filled"
              size="medium"
              startIcon={<LineSmileIcon />}
              endIcon={<LineAngleRightIcon />}
              onClick={handleFamilyAndFriendsDiscountClick}
            >
              {familyAndFriendsDiscountLabel}
            </DesktopFamilyAndFriendsDiscountChip>}
            <HeroText>
              <Group direction="vertical" gap={8} tabletGap={24}>
                <Pill palette="success" style={{ alignSelf: 'flex-start' }}>
                  {exclusiveToLEText}
                </Pill>

                <VerticalSpacer gap={8}>
                  <CSSBreakpoint max="mobile">
                    <Heading variant="heading2" as="h1" colour="dark-contrast">
                      {heroHeadingMessage}
                    </Heading>
                  </CSSBreakpoint>
                  <CSSBreakpoint min="tablet">
                    <Heading variant="heading1" as="h1" colour="dark-contrast">
                      {heroHeadingMessage}
                    </Heading>
                  </CSSBreakpoint>
                  {isReturnFlightFreeFeatureEnabled && <HeroReturnFlightNote variant="large" colour="dark-contrast">
                    <em>{returnFlightFreeNoteText}</em>
                  </HeroReturnFlightNote>}
                </VerticalSpacer>

                {isCharterLaunchOffer100Enabled && <CSSBreakpoint min="tablet">
                  <HeroCtaRow direction="horizontal" gap={12} verticalAlign="center">
                    <TextButton
                      kind="secondary"
                      variant="default"
                      size="large"
                      onClick={handleBrowseAllOffersClick}
                    >
                      {browseAllBundlesText}
                    </TextButton>
                    {!isAgentHubEnabled && <>
                      <BodyText variant="large" colour="neutral-eight">
                        {orText}
                      </BodyText>
                      <HeroPhoneLink
                        href={MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF}
                        rel="nofollow"
                      >
                        <LinePhoneIcon />
                        <Heading variant="heading3" as="span" colour="neutral-eight">
                          {MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_DISPLAY}
                        </Heading>
                      </HeroPhoneLink>
                    </>}
                  </HeroCtaRow>
                </CSSBreakpoint>}

                {canShowMobileFamilyAndFriendsDiscountChip && <CSSBreakpoint max="mobile">
                  <MobileFamilyAndFriendsDiscountChip
                    variant="filled"
                    size="medium"
                    startIcon={<LineSmileIcon />}
                    endIcon={<LineAngleRightIcon />}
                    onClick={handleFamilyAndFriendsDiscountClick}
                  >
                    {familyAndFriendsDiscountLabel}
                  </MobileFamilyAndFriendsDiscountChip>
                </CSSBreakpoint>}
              </Group>
            </HeroText>

            <CSSBreakpoint min="tablet">
              {desktopHeroCtaCard}
            </CSSBreakpoint>
          </HeroContent>

          <CSSBreakpoint max="mobile">
            {mobileHeroCtaCard}
          </CSSBreakpoint>
        </StyledHeroImage>
      </div>
      {hasTourBundleOffers && !isAgentHubEnabled && (
        <CSSBreakpoint min="tablet">
          <LayoutContainer relative>
            <OfferModeToggleWrapper id="maldives-direct-mode-toggle-desktop" className={!heroInView ? 'full-size' : undefined}>
              <ModeToggleTabs
                variant="desktop"
                isTourBundleMode={isTourBundleMode}
                onHotelsModeClick={handleHotelsModeClick}
                onTourBundlesModeClick={handleTourBundlesModeClick}
                showFullSize={!heroInView}
                modeToggleScale={modeToggleScale}
                modeToggleRef={modeToggleRef}
              />
            </OfferModeToggleWrapper>
          </LayoutContainer>
          <ModeToggleBottomSpacer />
        </CSSBreakpoint>
      )}

      {/* USP Section */}
      <USPSection $hasMobileReturnFlightFreeCard={isReturnFlightFreeFeatureEnabled}>
        <CSSBreakpoint min="tablet">
          <LayoutContainer>
            <VerticalSpacer gap={40}>
              <Heading variant="heading2" as="h2" align="start" tabletAlign="center">
                <FormattedMessage
                  id="C70J4bxwqoA1AzIjCwTP"
                  defaultMessage="Less time flying, {br}more time in paradise"
                  description="Unique selling proposition section heading text on the Maldives Direct search results page"
                  values={{
                    br: <br />,
                  }}
                />
              </Heading>

              <USPGrid $itemCount={displayedUspsCombined.length}>
                {displayedUspsCombined.map(uspItem => (
                  <CharterBundleLandingPageUspItem
                    key={uspItem.uspId}
                    title={uspItem.title}
                    description={uspItem.description}
                    icon={uspItem.icon}
                    tabletIcon={uspItem.tabletIcon}
                  />),
                )}
              </USPGrid>

              {!agentHubOrSubBrandConfig.isEnabled && <VerticalSpacer gap={2}>
                <USPFooter direction="vertical" gap={8} verticalAlign="center" tabletDirection="horizontal" tabletGap={16} tabletHorizontalAlign="center">
                  <BodyText variant="large">
                    <FormattedMessage
                      id="tzfCzmsUZ7nLhW1PkUqK"
                      defaultMessage="Our Maldives travel experts can help with any questions. <strong>Call our priority line</strong>"
                      description="USP footer text encouraging contact with Maldives charter experts"
                      values={{
                        strong: (chunks) => <strong>{chunks}</strong>,
                      }}
                    />
                  </BodyText>
                  <MaldivesSupportPhoneNumber />
                </USPFooter>
              </VerticalSpacer>}
            </VerticalSpacer>
          </LayoutContainer>
        </CSSBreakpoint>

        <CSSBreakpoint max="mobile">
          <LayoutContainer>
            <VerticalSpacer gap={16}>
              <Heading variant="heading3" as="h3" align="start" tabletAlign="center">
                <FormattedMessage
                  id="2Nm93gUJXNYfB8F6uwqa"
                  defaultMessage="Less time flying, {br}more time in paradise"
                  description="USP section heading on Maldives Direct search page - mobile"
                  values={{
                    br: <br />,
                  }}
                />
              </Heading>

              <Group direction="vertical" gap={4}>
                {displayedMobileUsps.map(uspItem => (
                  <BodyTextBlock
                    variant="medium"
                    key={uspItem.uspId}
                    startIcon={uspItem.icon}
                    colour={isRebrand ? 'highlight-primary-dark-contrast' : 'normal-contrast'}
                  >
                    {uspItem.description}
                  </BodyTextBlock>
                ))}
              </Group>

              <CharterPartnerIcon />
            </VerticalSpacer>
          </LayoutContainer>
        </CSSBreakpoint>
      </USPSection>
      {hasTourBundleOffers && !isAgentHubEnabled && (
        <MobileOfferModeToggleStickyWrapper max="mobile">
          <OfferModeToggleWrapper id="maldives-direct-mode-toggle-mobile">
            <ModeToggleTabs
              variant="mobile"
              isTourBundleMode={isTourBundleMode}
              onHotelsModeClick={handleHotelsModeClick}
              onTourBundlesModeClick={handleTourBundlesModeClick}
            />
          </OfferModeToggleWrapper>
        </MobileOfferModeToggleStickyWrapper>
      )}

      {/* Bundle Search Section */}
      {!isTourBundleMode && <MaldivesCharterFilters
        searchState={{
          rooms,
          selectedBundle,
          departureAirport: departureAirport,
          flightClass: fareClass,
          luxPlusEnabled,
          checkInDate,
          checkOutDate,
          departDate,
          returnDate,
        }}
        onRoomsChange={setRooms}
        onRoomsApply={checkAndResetFareClassIfNeeded}
        onBundleChange={handleBundleChange}
        onDateSelect={handleDateSelect}
        onAirportChange={handleAirportChange}
        onFlightClassChange={handleFlightClassChange}
        onOpenFlightCalendar={handleOpenFlightCalendar}
        availableFareClasses={availableFareClasses}
        rewardSeatsOnly={rewardSeatsOnly}
        onRewardSeatsOnlyChange={setRewardSeatsOnly}
        onCabinFilterChange={setCabinFilter}
      />}

      {/* Offers Section - using same data flow as HotelSearchPage */}
      <OffersSection id={MALDIVES_DIRECT_OFFERS_SECTION_ID}>
        <OffersContainer>
          <VerticalSpacer gap={32}>
            {isTourBundleMode && (
              <Heading id={MALDIVES_DIRECT_SEARCH_FILTERS_HEADING_ID} variant="heading2" as="h2" align="center">
                {tourModeBundlesHeading}
              </Heading>
            )}
            {!isTourBundleMode && <ReturnCharterFlightTile
              route={MALDIVES_CHARTER_ROUTE}
              fareClass={fareClassForSearch}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              occupancies={rooms}
              flightOrigin={departureAirport.code === MELBOURNE_AIRPORT.code ? undefined : departureAirport}
              onViewCharterDates={handleOpenFlightCalendar}
            />}
            {!isTourBundleMode && <MobileFilterTabsPadding>
              <AnywhereFilterTabs
                filters={bundleFilters}
                search={windowSearch}
              />
            </MobileFilterTabsPadding>}
            <OffersHeader direction="vertical" gap={8} tabletDirection="horizontal" tabletVerticalAlign="center" tabletHorizontalAlign="space-between">
              {isTourBundleMode ? tourOffersHeaderNode : offersHeaderNode}
              {!isTourBundleMode && <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between" wrap="nowrap">
                <OfferSortControl
                  sortOptions={maldivesSortOptions}
                  onSortChange={handleSortChange}
                />
                <HotelPriceToggle value={priceMode} onChange={handlePriceModeChange} />
              </Group>}
            </OffersHeader>
          </VerticalSpacer>
          {isTourBundleMode && (
            <OfferList
              filters={tourBundleFilters}
              tileStyle="search"
              pageSize={tourBundlePageSize}
              showLoadingTiles={tourBundleOfferList.fetching || tourBundleOfferList.offerIds.length === 0}
              disableUseOfferList
              extraOfferListAndAction={{ action: OfferListAction.OVERRIDE, offerList: tourBundleOfferList }}
              overrideFetching={tourBundleOfferList.fetching}
              minRequiredOffers={tourBundlePageSize}
            />
          )}
          {!isTourBundleMode && (
            <MaldivesDirectBundleProvider value={bundleContextValue}>
              <OfferList
                filters={bundleFilters}
                tileStyle="search"
                pageSize={pageSize}
                onPageChange={setOfferPage}
                minRequiredOffers={minRequiredOffers}
                disableUseOfferList={!canStreamHotelOffers}
              />
            </MaldivesDirectBundleProvider>
          )}
        </OffersContainer>
      </OffersSection>

      {!hasMoreStreamingOffers && <DreamBlock
        tiles={dreamTiles}
        title={intl.formatMessage({
          id: 'V-qmg9wDsPt0QYJmClE6',
          defaultMessage: 'Dream about the Maldives',
          description: 'Title for the dream block section on Maldives Direct search page',
        })}
      />}

      {/* Contact Information Banner */}
      <ContactBannerContainer>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" verticalAlign="center" gap={16}>
            <BodyText variant="large">
              {contactBannerText}
            </BodyText>
            <MaldivesSupportPhoneNumber />
          </Group>
          {isCharterLaunchOffer100Enabled && (
            <Group direction="vertical" gap={4}>
              <BodyText variant="small" weight="bold" colour="light-foreground-alt">
                {hundredDollarSaleTermsTitle}
              </BodyText>
              <BodyText variant="small" colour="light-foreground-alt">
                {hundredDollarSaleTermsText}
              </BodyText>
              <BodyText variant="small" colour="light-foreground-alt">
                <FormattedMessage
                  id="RC6pZSPR_AjCgmWZRMp0"
                  defaultMessage="Full T&Cs: <link>{fullTermsUrl}</link>"
                  description="Full terms and conditions link for the Maldives charter hundred dollar sale"
                  values={{
                    fullTermsUrl: HUNDRED_DOLLAR_SALE_FULL_TERMS_URL,
                    link: (chunks) => (
                      <TextLink href={HUNDRED_DOLLAR_SALE_FULL_TERMS_URL} target="_blank" rel="noopener noreferrer" weight="regular">
                        {chunks}
                      </TextLink>
                    ),
                  }}
                />
              </BodyText>
            </Group>
          )}
          <BodyText variant="small" colour="light-foreground-alt">
            {disclaimerText}
          </BodyText>
        </Group>
      </ContactBannerContainer>

      <MaldivesDirectSearchCalendarModal
        isOpen={isFlightCalendarOpen}
        title={selectTripDatesText}
        onClose={handleCloseFlightCalendar}
        onDateSelect={handleFlightDateSelect}
        nights={nights}
        rooms={rooms}
        luxPlusEnabled={luxPlusEnabled}
        dateRangesByMonth={dateRangesByMonth}
        selectedMonth={selectedMonth}
        onMonthChange={handleMonthChange}
        fareClassForSearch={fareClassForSearch}
        selectedFareClass={fareClass ?? null}
        priceMode={priceMode}
        onPriceModeChange={handlePriceModeChange}
        selectedOptionId={`${checkInDate}-${nights}n`}
        canStreamHotelOffers={canStreamHotelOffers}
        onDurationChange={handleDurationChangeFromCalendar}
        durationOptions={charterBundleDurationOptions}
        onLuxPlusToggle={handleLuxPlusToggle}
        onFareClassChange={handleFlightClassChange}
        availableFareClasses={allowedFareClassesForModal}
        rewardSeatsOnly={rewardSeatsOnly}
        cabinFilter={selectedCabin}
        onCabinFilterChange={setCabinFilter}
        onRewardSeatsOnlyChange={setRewardSeatsOnly}
        infiniteMemberOnly={infiniteMemberOnlyForTier}
        onInfiniteMemberOnlyChange={setInfiniteMemberOnly}
        feederOriginAirportCode={departureAirport.code}
      />

      {/* Floating Call Prompt - appears when hero is out of view */}
      <MaldivesCallPrompt
        show={!heroInView && !isCallPromptClosed && !agentHubOrSubBrandConfig.isEnabled}
        onClose={() => setIsCallPromptClosed(true)}
      />
    </div>
  )
}

export default MaldivesDirectSearchPage
